import richTextUtils from "../../utils/richtext.utils";
import i18n from "@plugins/i18n";
import DOMUtils from "@utils/dom.utils";
import {
  PICKER_ERROR,
  CONFIG,
  CONTEXT,
  FILE_TYPE,
  TYPE_CONTEXT,
  EVENT_ACTION,
} from "@plugins/google/google.constant";
import appConfigs from "@configs/app";
import GoogleAttachmentService from "@plugins/richtext/service/google.attachment.service";
import GooglePicker from "@plugins/google/google.picker";
import { googleDrive } from "@plugins/google/google.drive";
import { GoogleAuthEventBus } from "@plugins/google/google.auth.event.bus";

export default function (editor) {
  const ATTACHMENT_SELECTOR = "div.google-attachment";

  const richTextEditor = editor.options.get("richTextEditor");
  const context = richTextEditor.richTextContext;
  let googleAttachmentService = null;
  let documentContext = null;
  let typeContext = "";
  let eventAction = "";
  let googlePicker = null;
  let modalId = null;

  GoogleAuthEventBus.$on("handleCallback", handleCallback);
  // to close picker(if open) when route changes.
  GoogleAuthEventBus.$on("handleRouteChange", handleRouteChange);

  if (
    context.hasGoogleDriveAccess &&
    (richTextEditor.kind === "teacher_tinymce_10" || richTextEditor.kind === "teacher_tinymce_18")
  ) {
    documentContext = CONTEXT.LESSON;
    typeContext = TYPE_CONTEXT.TEXT_MEDIA;
    editor.ui.registry.addMenuButton("google", {
      tooltip: i18n.t("tinymce_plugin_google_upload_google_document_tooltip"),
      icon: "google",
      fetch: (callback) => {
        const items = [
          {
            type: "menuitem",
            text: i18n.t("tinymce_plugin_google_add_from_google_button_tooltip"),
            onAction: () => {
              openAddFromGoogle();
            },
          },
          {
            type: "menuitem",
            text: i18n.t("tinymce_plugin_google_embed_from_google_button_tooltip"),
            onAction: () => {
              openEmbedFromGoogle();
            },
          },
        ];
        callback(items);
      },
    });
  } else if (context.hasGoogleDriveAccess && richTextEditor.kind === "student_tinymce_6") {
    documentContext = CONTEXT.NOTEBOOK;
    typeContext = TYPE_CONTEXT.TEXT_MEDIA;
    editor.ui.registry.addToggleButton("google", {
      tooltip: i18n.t("tinymce_plugin_google_embed_from_google_button_tooltip"),
      icon: "google",
      onAction: function () {
        openEmbedFromGoogle();
      },
    });
  } else if (context.hasGoogleDriveAccess && richTextEditor.kind === "teacher_tinymce_17") {
    documentContext = CONTEXT.LESSON;
    typeContext = TYPE_CONTEXT.TEXT_MEDIA;

    editor.ui.registry.addToggleButton("google", {
      tooltip: i18n.t("tinymce_plugin_google_embed_from_google_button_tooltip"),
      icon: "google",
      onAction: function () {
        openEmbedFromGoogle();
      },
    });
  } else if (context.hasGoogleDriveAccess) {
    // Pre-populated Student Response, Student Attempt File Submission Question

    if (
      richTextEditor.kind === "teacher_tinymce_7" ||
      richTextEditor.kind === "teacher_tinymce_9" ||
      richTextEditor.kind === "teacher_tinymce_16"
    ) {
      documentContext = CONTEXT.LESSON;
      typeContext = TYPE_CONTEXT.FRQ;
    } else if (
      richTextEditor.kind === "student_tinymce_2" ||
      richTextEditor.kind === "teacher_tinymce_11" ||
      richTextEditor.kind === "student_teacher_tinymce_4"
    ) {
      typeContext = TYPE_CONTEXT.TEACHER_FEEDBACK;
      documentContext = CONTEXT.ATTEMPT;
    }

    editor.ui.registry.addToggleButton("google", {
      tooltip: i18n.t("tinymce_plugin_google_add_from_google_button_tooltip"),
      icon: "google",
      onAction: function () {
        if (
          richTextEditor.getUploadedGoogleAttachmentCount() >=
          richTextEditor.config.plugins.google.googleServiceConfig.googleAttachmentLimit
        ) {
          showMaximumUploadModal();
        } else {
          openAddFromGoogle();
        }
      },
    });
  }

  editor.addCommand("mceGoogleDelete", (targetNode) => {
    richTextUtils.removeNodeFromEditor(editor, targetNode);
  });

  (function init() {
    richTextEditor.plugins["google"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
      handlePluginRerender,
      insertMarkGoogleAttachment,
      openAddFromGoogle,
      openEmbedFromGoogle,
      duplicateGoogleAttachmentForAttempt,
      renderDuplicatedInlineAttachment,
    };
    googleAttachmentService = new GoogleAttachmentService(context);
  })();

  function renderDuplicatedInlineAttachment(oldGoogleDocumentId, newGoogleDocumentId) {
    const htmlContent = editor.getContent();
    const container = DOMUtils.createElement("div");
    container.innerHTML = htmlContent;

    const existingGoogleComponent = document.querySelector(
      `.google-attachment-tag[data-id='${oldGoogleDocumentId}']`,
    ).parentNode;

    const newGoogleElementData = {
      id: newGoogleDocumentId,
      dataContext: CONTEXT.ATTEMPT,
      dataType: CONFIG.ADD,
      processStatus: "processed",
    };

    richTextUtils.replaceGoogleAttachmentInEditorForAttempt(
      editor,
      newGoogleElementData,
      existingGoogleComponent,
      "google-attachment-inline-placeholder",
    );

    richTextUtils.renderInlineGoogleAttachment(
      editor,
      false,
      ".google-attachment-inline-placeholder",
      true,
      {
        onDuplicate: duplicateHandler,
        onRemove: removeHandler,
      },
    );

    editor.save();
  }

  function openAddFromGoogle() {
    eventAction = EVENT_ACTION.OPEN_ADD_FROM_GOOGLE;
    const options = {
      pickerCallback: handleUploadDocPicked,
    };
    googlePicker = new GooglePicker(options);
    googlePicker.loadPicker(typeContext).then(
      () => {
        // Success handler: add logic here if needed
      },
      (error) => {
        //Add log for debugging
        console.log(error);
        if (error === PICKER_ERROR.EMPTY_EMAIL) {
          openNotLinkedModal();
        }
      },
    );
  }

  function openEmbedFromGoogle() {
    eventAction = EVENT_ACTION.OPEN_EMBED_FROM_GOOGLE;
    const options = {
      pickerCallback: handleEmbedDocPicked,
    };
    googlePicker = new GooglePicker(options);
    googlePicker.loadPicker(CONFIG.EMBED).then(
      () => {
        // Success handler: add logic here if needed
      },
      (error) => {
        if (error === PICKER_ERROR.EMPTY_EMAIL) {
          openNotLinkedModal();
        }
      },
    );
  }

  function handleCallback() {
    switch (eventAction) {
      case EVENT_ACTION.OPEN_ADD_FROM_GOOGLE:
        openAddFromGoogle();
        break;
      case EVENT_ACTION.OPEN_EMBED_FROM_GOOGLE:
        openEmbedFromGoogle();
        break;
      default:
        break;
    }
  }

  function handleRouteChange() {
    if (googlePicker != null) googlePicker.closePicker();
  }

  function openNotLinkedModal() {
    // Blur the editor before opening the modal
    editor.getBody().blur();

    const props = {
      title: i18n.t("tinymce_plugin_not_linked_modal_title"),
      content: i18n.t("tinymce_plugin_not_linked_modal_message", {
        linkAccountUrl: appConfigs.vleDomain + `/profile/linked-account`,
      }),
      primaryBtnLabel: i18n.t("button_ok"),
      use_secondaryButton: false,
    };
    modalId = richTextEditor.$modal.showMessage("warning", props, true);
  }

  function showMaximumUploadModal() {
    const props = {
      title: i18n.t("tinymce_plugin_not_linked_modal_title"),
      content: i18n.t("tinymce_plugin_google_max_upload_modal_message"),
      primaryBtnLabel: i18n.t("button_ok"),
      use_secondaryButton: false,
    };
    modalId = richTextEditor.$modal.showMessage("warning", props, true);
  }

  function performOpenInNewTab(docId, mimeType) {
    let openInNewTabLink = "";

    switch (mimeType) {
      case FILE_TYPE.DOCUMENT.VALUE: {
        openInNewTabLink = `https://docs.google.com/document/d/${docId}/`;
        break;
      }
      case FILE_TYPE.SPREADSHEET.VALUE: {
        openInNewTabLink = `https://docs.google.com/spreadsheets/d/${docId}/`;
        break;
      }
      case FILE_TYPE.PRESENTATION.VALUE: {
        openInNewTabLink = `https://docs.google.com/presentation/d/${docId}/`;
        break;
      }
    }
    window.open(openInNewTabLink, "_blank");
  }

  function openGrantEditPermissionModal(docId, mimeType) {
    const props = {
      title: i18n.t("tinymce_plugin_not_edit_permission_modal_title"),
      content: i18n.t("tinymce_plugin_not_edit_permission_modal_message"),
      primaryBtnLabel: i18n.t("button_go_to_file"),
      secondaryBtnLabel: i18n.t("button_cancel"),
      actionPrimary: () => {
        performOpenInNewTab(docId, mimeType);
      },
    };
    modalId = richTextEditor.$modal.showMessage("warning", props, true);
  }

  async function checkUserEditPermission(documentId) {
    let isAbleToEdit = true;
    let mimeType = "";
    await googleDrive.getUserDriveFileData(documentId).then((response) => {
      if (!response.capabilities.canEdit) {
        isAbleToEdit = false;
        mimeType = response.mimeType;
      }
    });
    return [isAbleToEdit, mimeType];
  }

  function handleEmbedDocPicked(event) {
    // allow single selection only
    if (event.action === window.google.picker.Action.PICKED) {
      const googleDocument = {
        documentId: event.docs[0].id,
        mimeType: event.docs[0].mimeType,
        title: event.docs[0].name,
      };
      const toBePublished = true;
      checkUserEditPermission(googleDocument.documentId).then((response) => {
        if (response[0]) {
          if (documentContext === CONTEXT.LESSON) {
            googleAttachmentService.create(googleDocument, toBePublished).then(
              (data) => {
                richTextEditor.$modal.remove(modalId);
                richTextUtils.insertInlineGoogleAttachment(editor, {
                  id: data.id,
                  dataType: "embed",
                  dataContext: documentContext,
                  processStatus: "uploaded",
                });
                richTextUtils.renderInlineGoogleAttachment(
                  editor,
                  false,
                  ".google-attachment-inline-placeholder",
                  false,
                  {
                    onRemove: removeHandler,
                  },
                );
                editor.save();
              },
              (error) => {
                richTextEditor.passiveApiFailHandler(error);
              },
            );
          } else if (documentContext === CONTEXT.NOTEBOOK) {
            googleAttachmentService.createInNotebook(googleDocument, toBePublished).then(
              (data) => {
                richTextEditor.$modal.remove(modalId);
                richTextUtils.insertInlineGoogleAttachment(editor, {
                  id: data.id,
                  dataType: "embed",
                  dataContext: documentContext,
                  processStatus: "uploaded",
                });
                richTextUtils.renderInlineGoogleAttachment(
                  editor,
                  false,
                  ".google-attachment-inline-placeholder",
                  false,
                  {
                    onRemove: removeHandler,
                  },
                );
                editor.save();
              },
              (error) => {
                richTextEditor.passiveApiFailHandler(error);
              },
            );
          }
        } else {
          openGrantEditPermissionModal(googleDocument.documentId, response[1]);
        }
      });
    }
  }

  function handleUploadDocPicked(event) {
    // allow single selection only
    if (event.action === window.google.picker.Action.PICKED) {
      const googleDocument = {
        documentId: event.docs[0].id,
        mimeType: event.docs[0].mimeType,
        title: event.docs[0].name,
      };
      const toBePublished = false;
      checkUserEditPermission(googleDocument.documentId).then((response) => {
        if (response[0]) {
          if (documentContext === CONTEXT.LESSON) {
            googleAttachmentService.create(googleDocument, toBePublished).then(
              (data) => {
                richTextEditor.$modal.remove(modalId);
                richTextUtils.insertInlineGoogleAttachment(editor, {
                  id: data.id,
                  dataType: "add",
                  dataContext: documentContext,
                });
                richTextUtils.renderInlineGoogleAttachment(
                  editor,
                  false,
                  ".google-attachment-inline-placeholder",
                  false,
                  {
                    onDuplicate: duplicateHandler,
                    onRemove: removeHandler,
                  },
                );
                editor.save();
              },
              (error) => {
                richTextEditor.passiveApiFailHandler(error);
              },
            );
          } else if (documentContext === CONTEXT.ATTEMPT) {
            googleAttachmentService.createInStudentAttempt(googleDocument, false).then(
              (data) => {
                richTextEditor.$modal.remove(modalId);
                richTextUtils.insertInlineGoogleAttachment(editor, {
                  id: data.id,
                  dataType: "add",
                  dataContext: documentContext,
                  processStatus: "uploaded",
                });
                richTextUtils.renderInlineGoogleAttachment(
                  editor,
                  false,
                  ".google-attachment-inline-placeholder",
                  false,
                  {
                    onDuplicate: duplicateHandler,
                    onRemove: removeHandler,
                  },
                );
                editor.save();
              },
              (error) => {
                richTextEditor.passiveApiFailHandler(error);
              },
            );
          }
        } else {
          openGrantEditPermissionModal(googleDocument.documentId, response[1]);
        }
      });
    }
  }

  function insertMarkGoogleAttachment(data) {
    richTextUtils.insertInlineGoogleAttachment(editor, {
      id: data.id,
      dataType: "add",
      dataContext: CONTEXT.ATTEMPT,
      processStatus: "processed",
      usage: data.usage,
    });

    richTextUtils.renderInlineGoogleAttachment(
      editor,
      false,
      ".google-attachment-inline-placeholder",
      false,
      {
        onRemove: removeHandler,
      },
    );
    editor.save();
    richTextEditor.$emit("save-comment-set-edit-state");
  }

  function renderInEditor() {
    // render google attachment into editor if there are marked google file
    const googleDocumentProps = richTextEditor.getGoogleDocumentProps();
    if (isDefined(googleDocumentProps) && googleDocumentProps.id > 0) {
      insertMarkGoogleAttachment(googleDocumentProps);
      return;
    }

    richTextUtils.renderInlineGoogleAttachment(editor, false, ATTACHMENT_SELECTOR, false, {
      onDuplicate: duplicateHandler,
      onRemove: removeHandler,
    });
  }

  function cleanUp(content) {
    const container = document.createElement("div");
    const containerAfterCleanUp = document.createElement("div");
    container.innerHTML = content;
    containerAfterCleanUp.innerHTML = content;

    // remove placeholder
    DOMUtils.elOf(containerAfterCleanUp, ".google-attachment-wrapper").forEach((element) => {
      if (DOMUtils.elOf(element, ".inline-placeholder").length !== 0) {
        element.remove();
      }
    });
    DOMUtils.elOf(container, ".google-attachment-tag").forEach((element) => {
      cleanUpAttachmentTagElement(containerAfterCleanUp, element);
    });

    return containerAfterCleanUp.innerHTML;
  }

  function cleanUpAttachmentTagElement(container, element) {
    const id = element.getAttribute("data-id");
    const media = DOMUtils.elOf(container, `.google-attachment-tag[data-id='${id}']`)[0];
    const mediaWrapper = media.closest(".google-attachment-wrapper");

    // switch back the class
    DOMUtils.removeClass(element, "google-attachment-tag");
    DOMUtils.addClass(element, `google-attachment`);
    mediaWrapper.replaceWith(element);
  }

  function containPluginElements(container) {
    return container.querySelectorAll(".google-attachment").length > 0;
  }

  function handlePluginRerender(target) {
    const mediaComponent = target.closest(".google-attachment-wrapper");
    if (!mediaComponent || mediaComponent.length === 0) return;
    const componentElement = DOMUtils.elOf(mediaComponent, ".google-attachment-component");
    if (!componentElement || componentElement.length === 0 || componentElement[0].__vue__) return;
    const tagElement = DOMUtils.elOf(mediaComponent, ".google-attachment-tag")[0];
    cleanUpAttachmentTagElement(editor.getBody(), tagElement);

    renderInEditor();
  }

  // unused function, commented out for now
  // function generateTitle(title) {
  //   richTextUtils.generateTitle(
  //     googleAttachmentMetadata.title,
  //     context.activityTeamName,
  //     context.studentName,
  //   );
  // }

  async function removeHandler({ target, targetNode }) {
    if (targetNode) {
      editor.execCommand("mceGoogleDelete", targetNode);
    }

    // cater for cases when have undo action
    handlePluginRerender(target);
  }

  async function duplicateHandler({ documentId, id, mimeType, title }) {
    const requestData = {
      documentId: documentId,
      mimeType: mimeType,
      title: richTextUtils.generateTitle(title, context.activityTeamName, context.studentName),
    };
    const attemptGoogleDocumentId = await duplicateGoogleAttachmentForAttempt(requestData);
    const htmlContent = editor.getContent();
    const container = DOMUtils.createElement("div");
    container.innerHTML = htmlContent;
    const existingGoogleComponent = document.querySelector(
      `.google-attachment-tag[data-id='${id}']`,
    ).parentNode;
    const data = {
      id: attemptGoogleDocumentId,
      dataContext: CONTEXT.ATTEMPT,
      dataType: CONFIG.ADD,
      processStatus: "processed",
    };
    richTextUtils.replaceGoogleAttachmentInEditorForAttempt(
      editor,
      data,
      existingGoogleComponent,
      "google-attachment-inline-placeholder",
    );
    richTextUtils.renderInlineGoogleAttachment(
      editor,
      true,
      ".google-attachment-inline-placeholder",
      false,
      {
        onDuplicate: duplicateHandler,
        onRemove: removeHandler,
      },
    );
    editor.save();
  }

  function duplicateGoogleAttachmentForAttempt(requestData) {
    return new Promise((resolve) => {
      googleAttachmentService.createInStudentAttempt(requestData, true).then((data) => {
        return resolve(data.id);
      });
    });
  }
}
